import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueries } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import {
  Person,
  Search,
} from '@mui/icons-material';
import {
  Button,
  Loading,
  Alert,
} from '../design-system';
import { colors, typography } from '../design-system';
import { PlayerCareerTable as TablaEstadisticasJugador, PlayerCareerSummary as TotalesJugadorEstadisticas,
  PlayerStatisticsIdentity } from '../components/playerStatistics/PlayerStatistics';
import PlayerStatisticsLayout from '../components/playerStatistics/PlayerStatisticsLayout';
import { PlayerAdvancedCareer } from '../components/advancedStatistics/AdvancedStatistics';
import FilterTemporada from '../components/FilterTemporada';
import BuscarJugadorOEquipo from '../components/BuscarJugadorOEquipo';
import ExportarPDF from '../components/ExportarPDF';
import { useAppContext } from '../context/AppContext';
import {
  useEstadisticasJugadorAllSeasons,
  useJugador,
  useTemporadas,
} from '../hooks/queries';
import { competicionByIdTemporadaAndIdQueryOptions } from '../hooks/queries/useCompeticiones';
import usePageTitle from '../hooks/usePageTitle';
import usePageTour from '../hooks/usePageTour';

/**
 * EstadisticasJugadoresPageModern - Modern player statistics page
 * Comprehensive player stats with filtering and export functionality
 */
const EstadisticasJugadoresPageModern = () => {
  usePageTour('estadisticasJugadores');
  const { t } = useTranslation('stats');
  usePageTitle(t('players.headerTitle'));
  const navigate = useNavigate();
  const location = useLocation();
  const refPDF = useRef();
  const { jugador, setJugador } = useAppContext();

  const [datosJugador, setDatosJugador] = useState(null);
  const [temporadaSeleccionada, setTemporadaSeleccionada] = useState(null);
  const [mostrarBuscador, setMostrarBuscador] = useState(!jugador && !location.state?.jugador &&
    !new URLSearchParams(location.search).has('idJugador'));

  const requestedId = location.state?.jugador?.idJugador ||
    new URLSearchParams(location.search).get('idJugador') || jugador?.idJugador;
  const {
    data: temporadas = [],
    isLoading: temporadasLoading,
    isError: isTemporadasError,
    refetch: refetchTemporadas,
  } = useTemporadas();
  const {
    data: rawStats,
    isLoading: statsLoading,
    isError: isStatsError,
    refetch: refetchStats,
  } = useEstadisticasJugadorAllSeasons(requestedId);
  const ageSeason = temporadaSeleccionada || (rawStats?.length
    ? Math.max(...rawStats.map(row => Number(row.idTemporada) || 0)) : location.state?.jugador?.idTemporada);
  const { data: edadData } = useJugador(requestedId, ageSeason, { enabled: Boolean(requestedId && ageSeason) });
  const latestStatistics = useMemo(() => rawStats?.reduce((latest, row) =>
    !latest || Number(row.idTemporada) > Number(latest.idTemporada) ? row : latest, null), [rawStats]);
  const selectedPlayer = requestedId && String(jugador?.idJugador) !== String(requestedId)
    ? location.state?.jugador || latestStatistics : jugador || location.state?.jugador || latestStatistics;
  const identityStatistics = rawStats?.find(row => String(row.idTemporada) === String(ageSeason));

  const competitionKeys = useMemo(() => {
    if (!rawStats?.length) {
      return [];
    }

    const seen = new Set();

    return rawStats.reduce((acc, estadistica) => {
      if (!estadistica?.idTemporada || !estadistica?.idCompeticion) {
        return acc;
      }

      const key = `${estadistica.idTemporada}-${estadistica.idCompeticion}`;
      if (seen.has(key)) {
        return acc;
      }

      seen.add(key);
      acc.push({
        idTemporada: estadistica.idTemporada,
        idCompeticion: estadistica.idCompeticion,
      });
      return acc;
    }, []);
  }, [rawStats]);

  const competicionQueries = useQueries({
    queries: competitionKeys.map(({ idTemporada, idCompeticion }) =>
      competicionByIdTemporadaAndIdQueryOptions(idTemporada, idCompeticion, {
        retry: false,
      })
    ),
  });

  // Extract stable data array from queries to avoid infinite re-renders
  const competicionesData = useMemo(() => {
    return competicionQueries.map((q) => q.data ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competicionQueries.map((q) => q.dataUpdatedAt).join(',')]);

  const competicionesMap = useMemo(() => {
    const competiciones = new Map();

    competitionKeys.forEach(({ idTemporada, idCompeticion }, index) => {
      const competicion = competicionesData[index];
      if (competicion?.competition) {
        competiciones.set(
          `${idTemporada}-${idCompeticion}`,
          competicion.competition
        );
      }
    });

    return competiciones;
  }, [competitionKeys, competicionesData]);

  useEffect(() => {
    if (
      location.state?.jugador &&
      jugador?.idJugador !== location.state.jugador.idJugador
    ) {
      setJugador(location.state.jugador);
    }
  }, [jugador?.idJugador, location.state?.jugador, setJugador]);

  const datosJugadorComputed = useMemo(() => {
    if (!rawStats) return null;

    return rawStats.map((estadistica) => {
      const competicion = competicionesMap.get(
        `${estadistica.idTemporada}-${estadistica.idCompeticion}`
      );

      return competicion
        ? { ...estadistica, competicion }
        : { ...estadistica };
    });
  }, [rawStats, competicionesMap]);

  useEffect(() => {
    setDatosJugador(datosJugadorComputed);
  }, [datosJugadorComputed]);

  useEffect(() => {
    if (edadData?.edad) {
      setJugador((prevJugador) => {
        if (!prevJugador) {
          return prevJugador;
        }

        const edadActualizada =
          edadData.fechaObtencion ||
          prevJugador.edadActualizada ||
          new Date().toISOString();

        if (
          prevJugador.edad === edadData.edad &&
          prevJugador.edadActualizada === edadActualizada
        ) {
          return prevJugador;
        }

        return {
          ...prevJugador,
          edad: edadData.edad,
          edadActualizada,
        };
      });
      return;
    }

    if (rawStats?.[0]?.edad) {
      setJugador((prevJugador) => {
        if (!prevJugador || prevJugador.edad) {
          return prevJugador;
        }

        return {
          ...prevJugador,
          edad: rawStats[0].edad,
        };
      });
    }
  }, [edadData, rawStats, setJugador]);

  const loading =
    statsLoading ||
    temporadasLoading ||
    (!!rawStats?.length && competicionQueries.some((query) => query.isLoading));
  const error =
    isStatsError || isTemporadasError
      ? t('players.errors.loadData')
      : null;

  const cleanFiltersAndBack = () => {
    navigate('/dashboard');
  };

  const onChangeTemporada = (temporada) => {
    setTemporadaSeleccionada(temporada);
  };

  const handleRetry = () => {
    if (requestedId) {
      void refetchStats();
    }
    void refetchTemporadas();
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <Loading variant="soccer" size="large" />
      </Box>
    );
  }

  return (
    <PlayerStatisticsLayout title={t('players.headerTitle')} onBack={cleanFiltersAndBack}
      headerTour="player-stats-header" actions={selectedPlayer && datosJugador &&
        <ExportarPDF refContenido={refPDF} nombreArchivo="Estadisticas-Jugador" />}>
      {error && (
        <Alert
          severity="error"
          sx={{ m: 2 }}
          action={
            <Button color="inherit" size="small" onClick={handleRetry}>
              {t('players.errors.retry')}
            </Button>
          }
        >
          {error}
        </Alert>
      )}
      <div className="ps-career-content">
        {selectedPlayer && <PlayerStatisticsIdentity jugador={{ ...selectedPlayer, ...identityStatistics,
          edad: edadData?.edad ?? selectedPlayer.edad }} season={identityStatistics?.idTemporada} />}
        {/* Botón visible para buscar otro jugador */}
        {selectedPlayer && datosJugador && !mostrarBuscador && (
          <Button
            data-tour="player-stats-search"
            variant="outlined"
            startIcon={<Search />}
            onClick={() => setMostrarBuscador(true)}
            className="ps-search"
          >
            {t('players.searchAnotherPlayer')}
          </Button>
        )}

        {/* Buscador visible cuando se activa o no hay jugador */}
        {(mostrarBuscador || !selectedPlayer || !datosJugador) && (
          <section className="dr-panel ps-section" data-tour="player-stats-search">
            <header><h2>{t('players.search.title')}</h2></header>
              <BuscarJugadorOEquipo
                onJugadorSeleccionado={(j) => {
                  setJugador(j);
                  setMostrarBuscador(false);
                  setDatosJugador(null);
                  navigate(`/estadisticas?${new URLSearchParams({ idJugador: j.idJugador ?? j.id })}`,
                    { replace: true, state: { jugador: j } });
                }}
              />
          </section>
        )}

        <div ref={refPDF}>
          {selectedPlayer && datosJugador ? (
            <div className="ps-career-content">
              {/* Filters */}
              <section className="dr-panel ps-section" data-tour="player-stats-filters">
                <header><h2>{t('players.filters.title')}</h2></header>
                  <FilterTemporada
                    temporadas={temporadas}
                    onChangeTemporada={onChangeTemporada}
                  />
              </section>

              {/* Stats Summary */}
              <PlayerAdvancedCareer records={datosJugador} season={temporadaSeleccionada} seasons={temporadas} />
              <TotalesJugadorEstadisticas
                jugador={selectedPlayer}
                estadisticas={datosJugador}
                temporadaSeleccionada={temporadaSeleccionada}
              />

              {/* Detailed Stats Table */}
              <section className="dr-panel ps-section" data-tour="player-stats-table">
                <header><h2>{t('players.detailedStats.title')}</h2></header>
                  <TablaEstadisticasJugador
                    estadisticas={datosJugador}
                    temporadas={temporadas}
                    temporadaSeleccionada={temporadaSeleccionada}
                  />
              </section>
            </div>
          ) : !selectedPlayer ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Person
                sx={{
                  fontSize: 80,
                  color: colors.primary[500],
                  marginBottom: 2,
                }}
              />
              <Typography variant="h5" fontWeight={typography.fontWeight.bold} gutterBottom>
                {t('players.emptyState.title')}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {t('players.emptyState.description')}
              </Typography>
            </Box>
          ) : null}
        </div>
      </div>
    </PlayerStatisticsLayout>
  );
};

export default EstadisticasJugadoresPageModern;
